import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import type {
  AddFamilyMemberBody,
  UpdateFamilyMemberBody,
} from '@/features/families/api';
import { mapFamilyMember } from '@/features/families/mappers';
import { queryKeys } from '@/query/keys';
import type { FamilyMemberViewModel } from '@/models/family';

export interface FamilyMemberMutations {
  readonly add: UseMutationResult<FamilyMemberViewModel | null, Error, AddFamilyMemberBody>;
  readonly update: UseMutationResult<
    FamilyMemberViewModel | null,
    Error,
    { memberId: string; body: UpdateFamilyMemberBody }
  >;
  readonly remove: UseMutationResult<undefined, Error, { memberId: string }>;
}

/**
 * Member-level mutations for a single family record. Each mutation
 * invalidates both the family detail query and the dedicated members
 * page-query so the section re-renders with the new state. The Add /
 * Update mutations also return the mapped view-model so callers (the
 * Add dialog, the Edit dialog) can close on success.
 */
export function useFamilyMemberMutations(guid: string): FamilyMemberMutations {
  const queryClient = useQueryClient();
  const invalidate = (): Promise<void> => {
    // Browse Families shows a member-count column, so refresh that list too.
    queryClient.invalidateQueries({ queryKey: ['recent-families'] }).catch(() => undefined);
    return queryClient.invalidateQueries({ queryKey: queryKeys.family(guid) });
  };

  const add = useMutation<FamilyMemberViewModel | null, Error, AddFamilyMemberBody>({
    meta: { successMessage: 'success.toast.title.family_member_added' },
    mutationFn: async (body) => {
      const dto = await apiClient.addFamilyMember({ guid, body });
      return mapFamilyMember(dto, guid);
    },
    onSuccess: () => invalidate(),
  });

  const update = useMutation<
    FamilyMemberViewModel | null,
    Error,
    { memberId: string; body: UpdateFamilyMemberBody }
  >({
    meta: { successMessage: 'success.toast.title.family_member_updated' },
    mutationFn: async ({ memberId, body }) => {
      const dto = await apiClient.updateFamilyMember({ guid, memberId, body });
      return mapFamilyMember(dto, guid);
    },
    onSuccess: () => invalidate(),
  });

  const remove = useMutation<undefined, Error, { memberId: string }>({
    meta: { successMessage: 'success.toast.title.family_member_removed' },
    mutationFn: ({ memberId }) =>
      apiClient.removeFamilyMember({ guid, memberId }),
    onSuccess: () => invalidate(),
  });

  return { add, update, remove };
}
